'use strict';

/**
 * StickerSetResolver
 * ------------------
 * Resolves a sticker set (a key like 'drafting-design', or a raw items array from a custom set) into a
 * sanitized, drawable list of gallery items, the same way ConnectionStyle.js resolves a kit's link
 * style. Shared by the server, the board source, and the preview view. It never throws and never
 * returns an empty list: a bad or unknown set falls back to the built-in default, so the gallery
 * always has something to show.
 *
 * Item shapes it accepts:
 *   { Collection, Name, Title, Pri?, Sec? }  - a library shape (phosphor / tabler / simple)
 *   { IDSticker, Name }                       - an uploaded sticker (Phase 3; carried through as-is)
 *
 * Routing precedence is the caller's job (per-kit -> per-context default -> built-in); this module
 * supplies contextDefaultKey() for the per-context piece.
 */

const libStickerSets = require('./StickerSets.js');

const _COLLECTIONS = ['phosphor', 'tabler', 'simple'];

function _hex(pValue, pFallback)
{
	return (typeof pValue === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(pValue)) ? pValue : pFallback;
}

// Sanitize one item to something the gallery can draw. Returns null for anything malformed so the
// caller can drop it. A library item needs a known collection + a kebab-case name; an upload item
// needs a positive IDSticker.
function _sanitizeItem(pItem)
{
	if (!pItem || typeof pItem !== 'object') { return null; }
	let tmpUploadID = parseInt(pItem.IDSticker, 10);
	if (!isNaN(tmpUploadID) && tmpUploadID > 0)
	{
		return { IDSticker: tmpUploadID, Name: String(pItem.Name || ('Sticker ' + tmpUploadID)) };
	}
	if (_COLLECTIONS.indexOf(pItem.Collection) < 0) { return null; }
	let tmpName = String(pItem.Name == null ? '' : pItem.Name).toLowerCase().replace(/[^a-z0-9-]/g, '');
	if (!tmpName) { return null; }
	let tmpOut = { Collection: pItem.Collection, Name: tmpName, Title: String(pItem.Title || tmpName) };
	let tmpPri = _hex(pItem.Pri, null);
	let tmpSec = _hex(pItem.Sec, null);
	if (tmpPri) { tmpOut.Pri = tmpPri; }
	if (tmpSec) { tmpOut.Sec = tmpSec; }
	return tmpOut;
}

function _setItems(pKey)
{
	let tmpSet = libStickerSets.Sets[pKey];
	return (tmpSet && Array.isArray(tmpSet.Items)) ? tmpSet.Items : null;
}

// Resolve a set key OR a raw items array to a sanitized, non-empty drawable item list.
function resolveStickerSet(pKeyOrItems)
{
	let tmpRaw = null;
	if (typeof pKeyOrItems === 'string') { tmpRaw = _setItems(pKeyOrItems); }
	else if (Array.isArray(pKeyOrItems)) { tmpRaw = pKeyOrItems; }
	let tmpClean = (tmpRaw || []).map(_sanitizeItem).filter(Boolean);
	if (!tmpClean.length)
	{
		tmpClean = (_setItems(libStickerSets.DEFAULT_SET_KEY) || []).map(_sanitizeItem).filter(Boolean);
	}
	return tmpClean;
}

// The default set for a board context when no kit names one: profiles are expressive, everything else
// (vision inspiration boards, whiteboards) is design-first.
function contextDefaultKey(pContext)
{
	return (pContext === 'profile') ? libStickerSets.PROFILE_SET_KEY : libStickerSets.DEFAULT_SET_KEY;
}

function isKnownSetKey(pKey)
{
	return !!(pKey && libStickerSets.Sets[pKey]);
}

// Sanitize an items array for storage (a custom set): drop malformed entries, no default fallback.
function sanitizeItems(pItems)
{
	return (Array.isArray(pItems) ? pItems : []).map(_sanitizeItem).filter(Boolean);
}

// Parse a custom-set key 'custom:<id>' to its numeric IDStickerSet, or 0 for a system key / anything else.
function customKeyId(pKey)
{
	let tmpMatch = /^custom:(\d+)$/.exec(String(pKey == null ? '' : pKey));
	return tmpMatch ? (parseInt(tmpMatch[1], 10) || 0) : 0;
}

// A key that can be stored as a kit/brand choice: a known system set, or a 'custom:<id>' reference. The
// custom id is not checked for existence here (a deleted set resolves to empty -> the board falls back).
function isUsableSetKey(pKey)
{
	return isKnownSetKey(pKey) || (customKeyId(pKey) > 0);
}

// A flat, ordered summary for pickers and the preview surface.
function listSetSummaries()
{
	return libStickerSets.SET_ORDER
		.filter((pKey) => !!libStickerSets.Sets[pKey])
		.map((pKey) =>
		{
			let tmpSet = libStickerSets.Sets[pKey];
			return { Key: pKey, Label: tmpSet.Label, Description: tmpSet.Description || '', Count: (tmpSet.Items || []).length };
		});
}

module.exports =
{
	resolveStickerSet,
	contextDefaultKey,
	isKnownSetKey,
	isUsableSetKey,
	sanitizeItems,
	customKeyId,
	listSetSummaries,
	DEFAULT_SET_KEY: libStickerSets.DEFAULT_SET_KEY,
	PROFILE_SET_KEY: libStickerSets.PROFILE_SET_KEY
};
